// INTERNAL MODULE — do not import from this file directly outside of
// `src/wysiwyg/formats/`. Consumers should import from `../formats` (i.e.
// `./index`), which additionally triggers the side-effect registration of the
// built-in formats (pdf, zpl). Importing this module directly yields an empty
// registry and will produce "Unknown format" errors at runtime.
import type { OutputFormat } from '../types';
import type { FormatDefinition, FormatCapabilities } from './types';

const registry = new Map<OutputFormat, FormatDefinition>();

export function registerFormat(def: FormatDefinition): void {
  if (registry.has(def.id)) {
    console.warn(`[FormatRegistry] Format "${def.id}" is already registered. Overwriting.`);
  }
  registry.set(def.id, def);
}

export function getFormat(id: OutputFormat): FormatDefinition {
  const def = registry.get(id);
  if (!def) {
    throw new Error(`[FormatRegistry] Unknown format: "${id}". Did you forget to import its registration module?`);
  }
  return def;
}

export function getFormatOrDefault(id: OutputFormat | undefined): FormatDefinition {
  if (id) {
    const def = registry.get(id);
    if (def) return def;
  }
  const fallback = registry.get('pdf') ?? Array.from(registry.values())[0];
  if (!fallback) {
    throw new Error(
      '[FormatRegistry] No formats are registered. Ensure the format registration modules are imported.',
    );
  }
  return fallback;
}

export function getAllFormats(): FormatDefinition[] {
  const all = Array.from(registry.values());
  return all.sort((a, b) => {
    if (a.recommended && !b.recommended) return -1;
    if (!a.recommended && b.recommended) return 1;
    return 0;
  });
}

export function getFormatCapabilities(id: OutputFormat | undefined): FormatCapabilities {
  return getFormatOrDefault(id).capabilities;
}

export function getFormatChooserOptions(): Array<{
  format: OutputFormat;
  displayName: string;
  dpi: number;
  dpiLabel: string;
  recommended: boolean;
  description: string;
}> {
  const options: Array<{
    format: OutputFormat;
    displayName: string;
    dpi: number;
    dpiLabel: string;
    recommended: boolean;
    description: string;
  }> = [];

  for (const def of getAllFormats()) {
    for (const dpiOpt of def.dpiOptions) {
      options.push({
        format: def.id,
        displayName: def.displayName,
        dpi: dpiOpt.value,
        dpiLabel: dpiOpt.label,
        recommended: Boolean(def.recommended && dpiOpt.recommended),
        description: dpiOpt.label,
      });
    }
  }

  return options;
}
